import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Lock, UserCircle } from 'lucide-react';
import { useLang, LANG_LABELS } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

export default function Header({ onCTA }) {
  const { lang, setLang, t } = useLang();
  const { isAuth, user, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const anchor = (id) => (isHome ? `#${id}` : `/#${id}`);

  const NAV_LINKS = [
    { id: 'accueil', label: t('nav.home'), href: '/', link: true },
    { id: 'apropos', label: t('nav.about'), href: anchor('apropos') },
    { id: 'trading', label: t('nav.trading'), href: '/trading', link: true },
    { id: 'plateforme', label: t('nav.platform'), href: anchor('plateforme') },
    { id: 'partenariat', label: t('nav.partnership'), href: anchor('partenariat') },
    { id: 'equipe', label: t('nav.team'), href: anchor('equipe') },
    { id: 'contact', label: t('nav.contact'), href: anchor('contact') },
  ];

  const others = ['fr', 'en', 'zh'].filter((l) => l !== lang);

  return (
    <header data-testid="site-header" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${(!isHome || scrolled) ? 'bg-brand-navy shadow-lg' : 'backdrop-blur-sm'}`} style={(!isHome || scrolled) ? undefined : { backgroundColor: 'rgba(11,20,73,0.85)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <img src="/assets/yuville-logo.png" alt="Yuville" className="w-12 h-12 object-contain transition-transform group-hover:scale-105" style={{ filter: 'brightness(0) invert(1)' }} />
            <div className="flex flex-col leading-none">
              <span className="text-white font-bold text-lg tracking-wide">YUVILLE</span>
              <span className="text-brand-yellow text-[10px] tracking-[0.25em] font-semibold">INTERNATIONAL INC</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              l.link ? (
                <Link key={l.id} to={l.href}
                  className="text-white/90 hover:text-brand-yellow text-sm font-medium tracking-wide transition-colors relative group whitespace-nowrap">
                  {l.label}
                  <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-brand-yellow group-hover:w-full transition-all duration-300" />
                </Link>
              ) : (
                <a key={l.id} href={l.href}
                  className="text-white/90 hover:text-brand-yellow text-sm font-medium tracking-wide transition-colors relative group whitespace-nowrap">
                  {l.label}
                  <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-brand-yellow group-hover:w-full transition-all duration-300" />
                </a>
              )
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-1 border border-white/20 rounded-full p-1">
              {others.map((l) => (
                <button key={l} onClick={() => setLang(l)}
                  data-testid={`header-language-switch-${l}`}
                  className="px-2.5 py-1 text-xs font-semibold text-white/80 hover:text-brand-navy hover:bg-brand-yellow rounded-full transition-colors"
                  aria-label={`Switch to ${LANG_LABELS[l].name}`}>
                  {LANG_LABELS[l].code}
                </button>
              ))}
            </div>
            {isAuth ? (
              <Link to={isAdmin ? '/admin' : '/extranet'} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-brand-yellow/40 text-brand-yellow text-xs font-semibold hover:bg-brand-yellow hover:text-brand-navy transition">
                <UserCircle className="w-4 h-4" /> {user?.displayName || user?.username}
              </Link>
            ) : (
              <Link to="/extranet/login" data-testid="header-extranet-link" className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/20 text-white/80 text-xs font-semibold hover:border-brand-yellow hover:text-brand-yellow transition">
                <Lock className="w-3.5 h-3.5" /> {t('ext.headerBtn')}
              </Link>
            )}
            <button onClick={onCTA} className="btn-yellow text-sm">{t('cta.quote')}</button>
          </div>

          <button data-testid="header-mobile-menu-button" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 border-t border-white/10 pt-4">
            <nav className="flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a key={l.id} href={l.href} onClick={() => setOpen(false)}
                  className="text-white/90 hover:text-brand-yellow text-sm font-medium">
                  {l.label}
                </a>
              ))}
              <div className="flex items-center gap-2 pt-2">
                {others.map((l) => (
                  <button key={l} onClick={() => { setLang(l); setOpen(false); }}
                    data-testid={`header-mobile-language-switch-${l}`}
                    className="px-3 py-1.5 text-xs font-semibold border border-white/30 text-white rounded-full hover:bg-brand-yellow hover:text-brand-navy transition">
                    {LANG_LABELS[l].code}
                  </button>
                ))}
              </div>
              {isAuth ? (
                <Link to={isAdmin ? '/admin' : '/extranet'} onClick={() => setOpen(false)}
                  data-testid="header-mobile-extranet-link"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-brand-yellow/40 text-brand-yellow text-xs font-semibold w-fit">
                  <UserCircle className="w-4 h-4" /> {user?.displayName || user?.username}
                </Link>
              ) : (
                <Link to="/extranet/login" onClick={() => setOpen(false)}
                  data-testid="header-mobile-extranet-link"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/20 text-white/80 text-xs font-semibold hover:border-brand-yellow hover:text-brand-yellow transition w-fit">
                  <Lock className="w-3.5 h-3.5" /> {t('ext.headerBtn')}
                </Link>
              )}
              <button onClick={() => { setOpen(false); onCTA(); }} className="btn-yellow text-sm w-fit mt-2">{t('cta.quote')}</button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
